import { useState, useEffect } from "react";
import { Users, MessageSquare, Phone, RefreshCw, Clock, Eye } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

const ACTION_ICONS = {
  note: { icon: MessageSquare, label: "Nota", color: "text-blue-600" },
  contacted: { icon: Phone, label: "Contattato", color: "text-emerald-600" },
  status_change: { icon: RefreshCw, label: "Cambio stato", color: "text-amber-600" },
  viewed: { icon: Clock, label: "Visualizzato", color: "text-zinc-400" },
};

export default function AgentActivityLog() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);

  useEffect(() => {
    axios.get(`${API}/activities/summary`, { params: { days } })
      .then(res => setData(res.data))
      .catch(() => {});
  }, [days]);

  if (!data || Object.keys(data.agents).length === 0) {
    return (
      <div className="border border-zinc-200 rounded-sm p-4">
        <h3 className="text-sm font-bold text-zinc-900 flex items-center gap-2">
          <Users className="w-4 h-4" />
          Attività Agenti
        </h3>
        <p className="text-xs text-zinc-400 mt-2">Nessuna attività registrata negli ultimi {days} giorni.</p>
      </div>
    );
  }

  return (
    <div data-testid="agent-activity-log" className="border border-zinc-200 rounded-sm p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-zinc-900 flex items-center gap-2">
          <Users className="w-4 h-4" />
          Attività Agenti
        </h3>
        <div className="flex gap-1">
          {[1, 7, 30].map(d => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-2 py-0.5 text-[10px] font-semibold rounded-sm transition-all ${
                days === d ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-500 hover:text-zinc-700"
              }`}
            >
              {d === 1 ? "Oggi" : `${d}gg`}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        {Object.entries(data.agents)
          .sort(([, a], [, b]) => b.total - a.total)
          .map(([agent, stats]) => (
            <div key={agent} className="border border-zinc-100 rounded-sm p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-bold text-zinc-800">{agent}</span>
                <span className="text-[10px] font-semibold text-zinc-400">{stats.total} azioni</span>
              </div>
              <div className="flex gap-3 mb-2 flex-wrap">
                {stats.views > 0 && (
                  <span className="flex items-center gap-1 text-[11px] text-zinc-500 font-semibold">
                    <Eye className="w-3 h-3" />{stats.views} visualizzati
                  </span>
                )}
                {stats.contacts > 0 && (
                  <span className="flex items-center gap-1 text-[11px] text-emerald-600 font-semibold">
                    <Phone className="w-3 h-3" />{stats.contacts} contattati
                  </span>
                )}
                {stats.notes > 0 && (
                  <span className="flex items-center gap-1 text-[11px] text-blue-600 font-semibold">
                    <MessageSquare className="w-3 h-3" />{stats.notes} note
                  </span>
                )}
                {stats.status_changes > 0 && (
                  <span className="flex items-center gap-1 text-[11px] text-amber-600 font-semibold">
                    <RefreshCw className="w-3 h-3" />{stats.status_changes} cambi stato
                  </span>
                )}
              </div>
              {stats.recent.length > 0 && (
                <div className="space-y-0.5 border-t border-zinc-100 pt-2 mt-1">
                  {stats.recent.map((act, i) => {
                    const cfg = ACTION_ICONS[act.action] || ACTION_ICONS.status_change;
                    const Icon = cfg.icon;
                    const isView = act.action === "viewed";
                    return (
                      <div key={i} className="flex items-start gap-2 text-[10px] text-zinc-500 py-0.5">
                        <Icon className={`w-3 h-3 shrink-0 mt-0.5 ${cfg.color}`} />
                        <div className="flex-1 min-w-0">
                          {/* ref_code + title preview */}
                          {(act.ref_code || act.property_title) && (
                            <div className="flex items-center gap-1 mb-0.5">
                              {act.ref_code && (
                                <span className="font-mono font-bold text-[9px] text-zinc-700 bg-zinc-100 px-1 py-0.5 rounded-sm shrink-0">{act.ref_code}</span>
                              )}
                              {act.property_title && (
                                <span className="text-zinc-600 font-medium truncate">{act.property_title}</span>
                              )}
                            </div>
                          )}
                          {/* detail — hide for views since icon+title is enough */}
                          {!isView && (
                            <span className="truncate block">{act.detail}</span>
                          )}
                        </div>
                        <span className="shrink-0 text-zinc-400 flex items-center gap-0.5 tabular-nums">
                          <Clock className="w-2.5 h-2.5" />{formatDate(act.timestamp)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
      </div>
    </div>
  );
}
